import { API_FIELD_MAP, SERVICE_DISPLAY_FIELD_TYPES } from "./offerFormConstants";

export function emptyLangs(langs) {
  return langs.reduce((acc, lang) => {
    acc[lang] = "";
    return acc;
  }, {});
}

export function readApiField(offer, formField) {
  const keys = API_FIELD_MAP[formField] || [formField];
  for (const key of keys) {
    if (offer[key] && typeof offer[key] === "object" && Object.keys(offer[key]).length > 0) {
      return offer[key];
    }
  }
  return {};
}

export function langMapsFromOfferServices(services, domainLanguages) {
  const maps = {};
  Object.keys(SERVICE_DISPLAY_FIELD_TYPES).forEach((key) => {
    maps[key] = {};
  });
  domainLanguages.forEach((lang) => {
    Object.entries(SERVICE_DISPLAY_FIELD_TYPES).forEach(([fieldKey, svcType]) => {
      const line = (services || [])
        .filter((s) => s.type === svcType)
        .map((s) => (s.name && (s.name[lang] || s.name.en)) || s.id)
        .join(", ");
      maps[fieldKey][lang] = line;
    });
  });
  return maps;
}

function mergeServiceTextWithDerived(formKey, form, derivedByLang, domainLanguages) {
  const out = {};
  for (const lang of domainLanguages) {
    const user = form[formKey] && form[formKey][lang];
    const u = user != null ? String(user).trim() : "";
    const d =
      derivedByLang && derivedByLang[lang] != null
        ? String(derivedByLang[lang]).trim()
        : "";
    out[lang] = u || d;
  }
  return out;
}

function normalizeOfferScore(form) {
  const s = form.score;
  if (s === null || s === undefined || s === "") return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function normalizePackagePrice(form) {
  const p = form.packagePrice;
  if (p === null || p === undefined || p === "") return 0;
  const n = Number(p);
  return Number.isFinite(n) ? n : 0;
}

export function normalizeCurrenciesList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.currencies)) return raw.currencies;
  return [];
}

export function buildCurrencyForOffer(currencyCode, currenciesList) {
  const list = normalizeCurrenciesList(currenciesList);
  const trimmed = currencyCode != null ? String(currencyCode).trim() : "";
  if (!trimmed) {
    return { name: "", code: "", rate: 0 };
  }
  const codeUpper = trimmed.toUpperCase();
  const found = list.find((c) => c && String(c.code).toUpperCase() === codeUpper);
  if (found) {
    const rate =
      found.rate != null && Number.isFinite(Number(found.rate)) ? Number(found.rate) : 0;
    const name = found.name != null ? String(found.name) : String(found.code);
    return { name, code: String(found.code), rate };
  }
  return { name: trimmed, code: trimmed, rate: 0 };
}

function templateIdValue(form) {
  if (form.template_id == null) return "";
  return String(form.template_id).trim();
}

function localizedEntityPayload(entity) {
  if (!entity || entity.id == null) return null;
  return {
    id: entity.id,
    name: typeof entity.name === "object" && entity.name ? entity.name : {},
    alias: typeof entity.alias === "object" && entity.alias ? entity.alias : {}
  };
}

export function findCountryRowByGeoCode(countries, countryCode) {
  if (countryCode == null || countryCode === "" || !Array.isArray(countries)) return null;
  const want = String(countryCode).trim().toLowerCase();
  return (
    countries.find(
      (c) => c && c.code != null && String(c.code).trim().toLowerCase() === want
    ) || null
  );
}

export function clinicPayloadForOffer(clinic, resolved = {}) {
  if (!clinic || clinic.id == null) return null;
  const { countryRow, cityRow } = resolved;
  const out = {
    id: clinic.id,
    name: typeof clinic.name === "object" && clinic.name ? clinic.name : {},
    alias: typeof clinic.alias === "object" && clinic.alias ? clinic.alias : {}
  };
  const country =
    localizedEntityPayload(clinic.country) || localizedEntityPayload(countryRow);
  if (country) out.country = country;
  const city = localizedEntityPayload(clinic.city) || localizedEntityPayload(cityRow);
  if (city) out.city = city;
  return out;
}

function pickFirstIllnessKind(illness) {
  if (!illness || typeof illness !== "object") return null;
  const single = illness.illnessKind || illness.illness_kind;
  if (single && single.id != null) return single;
  const lists = [illness.illnessKinds, illness.illness_kinds, illness.kinds];
  for (const list of lists) {
    if (Array.isArray(list) && list.length) {
      const first = list[0];
      if (first && typeof first === "object" && first.id != null) return first;
    }
  }
  if (illness.kind != null && illness.kind !== "") {
    const id = Number(illness.kind);
    if (Number.isFinite(id)) {
      return { id, alias: {}, name: {}, is_hide: false };
    }
  }
  return null;
}

export function normalizeIllnessForOfferPayload(illness) {
  if (!illness || illness.id == null) return null;
  const alias = typeof illness.alias === "object" && illness.alias ? { ...illness.alias } : {};
  const name = typeof illness.name === "object" && illness.name ? { ...illness.name } : {};
  const ik = pickFirstIllnessKind(illness);
  if (!ik || ik.id == null) return null;
  const kindId = Number(ik.id);
  if (!Number.isFinite(kindId)) return null;
  return {
    id: illness.id,
    alias,
    name,
    kind: kindId,
    illnessKind: {
      id: kindId,
      alias: typeof ik.alias === "object" && ik.alias ? { ...ik.alias } : {},
      name: typeof ik.name === "object" && ik.name ? { ...ik.name } : {},
      is_hide: !!ik.is_hide
    }
  };
}

export function linkedCatalogEntitiesForPayload(form) {
  const t = form.type;
  let illnesses = [];
  if (t === "illness" && Array.isArray(form.illnesses)) {
    illnesses = form.illnesses.map(normalizeIllnessForOfferPayload).filter(Boolean);
  }
  return {
    operations:
      t === "procedure" && Array.isArray(form.operations) ? [...form.operations] : [],
    illnesses
  };
}

export function buildOfferPayload(form, tagsInput, domainLanguages, currenciesList) {
  const derived = langMapsFromOfferServices(form.services, domainLanguages);
  const linked = linkedCatalogEntitiesForPayload(form);
  return {
    type: form.type,
    level: form.level,
    is_hide: form.is_hide,
    template_id: templateIdValue(form),
    tags: tagsInput
      ? tagsInput.split(",").map((t) => t.trim()).filter(Boolean)
      : [],
    name: form.name,
    for_who: form.for_who,
    duration: form.duration,
    procedure_description: form.procedure_description,
    accommodation: mergeServiceTextWithDerived(
      "accommodation",
      form,
      derived.accommodation,
      domainLanguages
    ),
    transport: mergeServiceTextWithDerived(
      "transport",
      form,
      derived.transport,
      domainLanguages
    ),
    other_services: mergeServiceTextWithDerived(
      "other_services",
      form,
      derived.other_services,
      domainLanguages
    ),
    implant: mergeServiceTextWithDerived("implant", form, derived.implant, domainLanguages),
    post_operation_care: form.post_operation_care,
    promo_description: form.promo_description,
    services: form.services,
    packagePrice: normalizePackagePrice(form),
    currency: buildCurrencyForOffer(form.currency_code, currenciesList),
    operations: linked.operations,
    illnesses: linked.illnesses
  };
}

export function buildOfferCreatePayload(form, tagsInput, domainLanguages, clinicPayload, currenciesList) {
  const patch = buildOfferPayload(form, tagsInput, domainLanguages, currenciesList);
  const m = (v) => ({ ...emptyLangs(domainLanguages), ...(v || {}) });
  const score = normalizeOfferScore(form);
  const tid = templateIdValue(form);
  const services = Array.isArray(patch.services) ? patch.services : [];
  const clinic = clinicPayload != null ? clinicPayload : clinicPayloadForOffer(form.clinic);

  return {
    clinic,
    type: patch.type,
    level: patch.level,
    is_hide: !!patch.is_hide,
    template_id: tid,
    templateId: tid,
    score,
    tags: patch.tags,
    name: m(patch.name),
    forWho: m(patch.for_who),
    for_who: m(patch.for_who),
    duration: m(patch.duration),
    procedureDescription: m(patch.procedure_description),
    procedure_description: m(patch.procedure_description),
    accommodation: m(patch.accommodation),
    transport: m(patch.transport),
    otherServices: m(patch.other_services),
    other_services: m(patch.other_services),
    postOperationCare: m(patch.post_operation_care),
    post_operationCare: m(patch.post_operation_care),
    implant: m(patch.implant),
    promo_description: m(patch.promo_description),
    services,
    illnesses: patch.illnesses,
    operations: patch.operations,
    diagnostics: [],
    doctors: [],
    equipments: [],
    durationHotel: 0,
    available_dates: [],
    available_languages: [],
    date_period: {},
    benefits: m({}),
    country_text: m({}),
    city_text: m({}),
    properties: [],
    internal_utp: "",
    group_type: "",
    advertising: null,
    packagePrice: patch.packagePrice,
    currency: patch.currency,
    specialPrice: {
      expires: "",
      forBookimedOnly: false,
      percent: 0,
      priceUsd: 0,
      price: 0,
      start_date: ""
    }
  };
}
